import { Button } from "@tm/components"
import { useWorkTask } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { channel, ConfigParams, RegisteredModels, ViewState } from "@tm/models"
import Morpheus, { BundleComponent } from "@tm/morpheus"
import { Container } from "@tm/nexus"
import { PropsWithChildren } from "react"
import { QueryFunctionContext, useQuery } from "react-query"
import { useHistory } from "react-router"

type ExportVoucherInfo = {
    uri?: string
    exportLabel?: string
}

type QueryKey = ["EXPORT-VOUCHER-INFO", string]

async function loadExportVoucherInfo({ queryKey: [, workTaskId] }: QueryFunctionContext<QueryKey>): Promise<ExportVoucherInfo> {
    const container = Container.getInstance<ViewState<ExportVoucherInfo | undefined>>(RegisteredModels.ViewState)

    const state = await container.subscribe(`${workTaskId}_EXPORT-VOUCHER`).load()

    return state?.value ?? {}
}

function useExportVoucherInfo(workTaskId: string | undefined): ExportVoucherInfo {
    const { data } = useQuery<ExportVoucherInfo, unknown, ExportVoucherInfo, QueryKey>(
        ["EXPORT-VOUCHER-INFO", workTaskId ?? ""],
        loadExportVoucherInfo,
        {
            cacheTime: Infinity,
            enabled: !!workTaskId,
        }
    )

    return data ?? {}
}

function checkExportVoucherButtonPosition(position: ConfigParams["exportVoucherConfiguration"]["position"]): boolean {
    const {
        exportVoucherConfiguration: { position: configuredPosition },
    } = Morpheus.getParams<ConfigParams>()

    return configuredPosition === position
}

/**
 * Use this to check if the export voucher button should be rendered.
 * @param position The position you want to render the button at.
 */
export function useExportVoucherButtonEnabled(
    workTaskId: string | undefined,
    position: ConfigParams["exportVoucherConfiguration"]["position"]
): boolean {
    const { uri } = useExportVoucherInfo(workTaskId)
    return !!uri && checkExportVoucherButtonPosition(position)
}

type Props = PropsWithChildren<{
    workTaskId: string
    /**
     * Set to `true` to disable the confirmation dialog
     * shown before the worktask is closed when the export button is clicked.
     */
    disableWorkTaskCloseConfirmation?: boolean
}>

/**
 * Renders the worktask export button for the DMS.
 * Use this from within other React components.
 */
export function ExportVoucherButton({ workTaskId, disableWorkTaskCloseConfirmation, children }: Props) {
    const {
        exportVoucherConfiguration: { closeWorkTask, dmsName },
    } = Morpheus.getParams<ConfigParams>()

    const { translate } = useLocalization()
    const history = useHistory()

    const { uri, exportLabel } = useExportVoucherInfo(workTaskId)

    function handleCloseWorkTask() {
        channel("WORKTASK", workTaskId).publish("WORKTASK/CLOSE", undefined)

        // Even if `window.location.href` is called below, calling `history.push` is needed to close the worktask,
        // in case the `exportUri` is a custom protocol handler, then the browser may not redirect NEXT to that URL,
        // but call the URL in the background. (see NEXT-29780)
        history.push("/")
    }

    function handleClick() {
        if (closeWorkTask) {
            if (disableWorkTaskCloseConfirmation) {
                handleCloseWorkTask()
            } else {
                channel("GLOBAL").publish("INTERCEPTABLE/WORKTASK_DELETE", {
                    workTaskId,
                    callback: handleCloseWorkTask,
                })
            }
        }

        // Don't use `window.open(exportUri!, "internal")` here to call this URL in the hidden iframe,
        // because browser based DMS require the user to be redirected back to their application.
        window.location.href = uri!
    }

    if (!uri) {
        return null
    }

    return (
        <Button onClick={handleClick}>
            {children}
            {exportLabel ?? dmsName ?? translate(1875)}
        </Button>
    )
}

/**
 * Renders the worktask export button for the DMS.
 * Do not export and use this. Only through Morpheus by including it in a ui-config.
 */
function ExportVoucherButtonForMorpheus() {
    const { translate } = useLocalization()
    const { workTaskId } = useWorkTask() ?? {}
    const renderExportVoucherButton = useExportVoucherButtonEnabled(workTaskId, "module-navigation")

    if (!workTaskId || !renderExportVoucherButton) {
        return null
    }

    return <ExportVoucherButton workTaskId={workTaskId}>{translate(1209)}</ExportVoucherButton>
}

const ExportVoucherButtonComponent: BundleComponent<void, typeof ExportVoucherButtonForMorpheus> = {
    name: "export-voucher-button",
    component: ExportVoucherButtonForMorpheus,
}

export default ExportVoucherButtonComponent
